// The Negotiation tab (Lane 1354, step S3; mockup v4 and Ryan's review, PLAN 13b):
// one supplier at a time, buyer only. Strategy (the goal, set on the Overview), where
// they stand, where the gap concentrates, and the targets the buyer can type over.
// Every figure is the server's (helpers/sourcing_negotiation.py); the browser only
// formats it and never adds figures up.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatAmount, formatUnitPrice } from './sourcingApi';
import NegotiationActions from './NegotiationActions';
import NegotiationIntel from './NegotiationIntel';
import StatTile from './StatTile';
import './negotiation.css';
import './goal.css';

const PAGE = 100;
const TARGET_RE = /^\d{1,10}(\.\d{0,4})?$/;

const BASIS_TEXT = {
  // The stored key keeps its first name; the target matches the best offer we received
  // from any other supplier, approved for the part or not (Lane 1395).
  qualified_alternative: 'Match best offer',
  hold_today: "Hold today's price",
  buyer: 'Your target',
  needs_buyer_target: 'Needs your target',
  competitive: 'Already competitive',
};

function pct(value, signed = true) {
  if (value === null || value === undefined) return null;
  const n = Number(value) * 100;
  if (!Number.isFinite(n)) return null;
  // Half up on the stored 4-decimal ratio, as the server's deck and brief show it (one
  // rule, Lane 1354 S5): toFixed on a binary float turns 1.15 into "1.1".
  const tenths = Math.round(Math.abs(Math.round(Number(value) * 10000)) / 10);
  const text = `${(tenths / 10).toFixed(1)}%`;
  if (!signed) return text;
  return n > 0 ? `+${text}` : n < 0 ? `-${text}` : text;
}

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function plural(n, one, many) { return `${n.toLocaleString()} ${n === 1 ? one : many}`; }

function deckDay(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

// Beside "Where they stand" the two cards are exactly the same height (Ryan, Lane 1362):
// the Strategy card takes the row's height and never sets it (`fit`), so longer text is
// clipped with a fade and a "Show more" button; shown in full, the row grows instead.
function Strategy({ goal, approach, onOpenOverview, fit }) {
  const body = useRef(null);
  const [more, setMore] = useState(false);
  const [clipped, setClipped] = useState(false);
  useEffect(() => {
    const el = body.current;
    if (!el || more || !fit) { setClipped(false); return undefined; }
    const check = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const watch = new ResizeObserver(check);
    watch.observe(el);
    return () => watch.disconnect();
  }, [more, fit, goal, approach]);
  return (
    <section className={`srce-card2 neg-strat-card${fit && !more ? ' neg-strat-card--fit' : ''}`} data-testid="neg-strategy">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Strategy</span>
        <span className="srce-pill srce-pill--gray">Your team only</span>
        {onOpenOverview && <button type="button" className="ll-clr srce-push neg-nowrap" onClick={onOpenOverview}>Change on Overview</button>}
      </div>
      <div ref={body} className={`neg-strat${clipped ? ' neg-strat--clipped' : ''}`}>
        <div>
          <span className="neg-k">Goal</span>
          {goal ? <p className={`srce-strong${goal.includes('\n') ? ' srce-goal-lines' : ''}`}>{goal}</p>
            : <p className="srce-mut">No goal yet. Set it on the Overview tab.</p>}
        </div>
        <div>
          <span className="neg-k">Approach</span>
          {approach ? <>
            <p data-testid="neg-approach">{approach.text}</p>
            <p className="srce-mut">Private: do not say. From your deck of {deckDay(approach.deck_at)}.</p>
            {approach.changed_since && <p className="srce-mut" data-testid="neg-approach-changed">Offers or targets changed since this deck. Build a new deck for a current brief.</p>}
          </> : <p className="srce-mut">Build the negotiation deck to get an approach.</p>}
        </div>
      </div>
      {(clipped || more) && (
        <button type="button" className="neg-lnk neg-more" aria-expanded={more}
          onClick={() => setMore(!more)}>{more ? 'Show less' : 'Show more'}</button>)}
    </section>
  );
}

// The shared stat tile (StatTile.js, Lane 1362): the same figure size and tone colours as
// the Overview's tiles.
function Tile({ k, v, s, tone }) {
  return <StatTile label={k} value={v} note={s} tone={tone} />;
}

// The gap's two parts in plain words: the negotiation deck's split by who supplies the
// part today (Lane 1379). The server sends the groups and their amounts already rounded
// the way the deck shows them, so the tile and the deck agree; nothing is added up here.
const SUPPLY_WORDS = {
  supplied: 'on parts they supply today',
  not_supplied: "on parts they don't supply today",
};

// What the gap leaves out, in plain words: a keep-today's-price target on a part where
// they are not today's confirmed supplier on every site (``supplies_today``: another
// supplier or an unknown one on a site; the deck counts only their own parts), and parts
// with no target yet. Without these, an empty gap would read as "at target" (Codex P2).
function leftOutText(leftOut) {
  const out = [];
  if (leftOut && leftOut.not_supplied) out.push(`${plural(leftOut.not_supplied, 'part', 'parts')} not counted: they are not today's confirmed supplier on every site`);
  if (leftOut && leftOut.no_target) out.push(`${plural(leftOut.no_target, 'part has', 'parts have')} no target price yet`);
  return out;
}

function Stand({ stand, supplierName, currency }) {
  const targetGap = stand.target_gap;
  const leftOut = leftOutText(stand.target_gap_left_out);
  const vs = stand.vs_today;
  const vsPct = pct(vs.pct_change);
  // The parts with a lower offer, and those with none, are not listed here (Ryan, Lane
  // 1362); the concentration card and the targets table show the parts.
  const notes = [];
  const bs = vs.by_state;
  if (bs.varies) notes.push(["Today's price varies", `${plural(bs.varies, 'part', 'parts')} (prices differ by site)`]);
  if (bs.partial) notes.push(["Today's price on some sites", `${plural(bs.partial, 'part', 'parts')} (not every site has a price today)`]);
  if (bs.missing) notes.push(["No price today", plural(bs.missing, 'part', 'parts')]);
  if (stand.not_usable) {
    notes.push(['Left out', `${plural(stand.not_usable, 'priced part', 'priced parts')}: the minimum order is above the event volume, or the calculation cannot use the part`]);
  }
  return (
    <section className="srce-card2 neg-stand" data-testid="neg-stand">
      <div className="srce-card2-h">
        <span className="srce-card2-t neg-ell" title={`Where ${supplierName} stands`}>Where {supplierName} stands</span>
      </div>
      <div className="srce-tiles neg-tiles">
        <Tile k="Gap to target prices" tone={targetGap ? 'crit' : null}
          v={targetGap ? targetGap.total : 'None'}
          s={(targetGap ? 'a year: ' : '') + ([...(targetGap ? targetGap.groups.map((g) => `${g.amount} ${SUPPLY_WORDS[g.key]} (${plural(g.parts, 'part', 'parts')})`) : []),
            ...leftOut].join(' · ') || 'Their offers are at or below our target prices')} />
        <Tile k="Against today's price" tone={vsPct && Number(vs.pct_change) > 0 ? 'crit' : vsPct ? 'good' : null}
          v={vsPct || 'Not comparable'}
          s={vsPct ? `on ${plural(vs.parts, 'part', 'parts')} with one price today` : 'No part has one price today'} />
        <Tile k="Best offer" v={`${stand.best_on.toLocaleString()} of ${stand.parts_usable.toLocaleString()}`}
          s={`parts where ${supplierName} is already the best`} />
        <Tile k="Total RFP value" v={formatAmount(stand.offer_total, currency, 0)} s="a year at event volumes" />
      </div>
      {notes.length > 0 && (
        <ul className="neg-notes">
          {notes.map(([k, v]) => <li key={k}><span>{k}</span><span>{v}</span></li>)}
        </ul>
      )}
    </section>
  );
}

function Concentration({ conc, currency, comparedParts }) {
  let big = null;
  let head;
  let sub;
  if (!conc.lower_parts) {
    head = 'No other offer is below theirs on any part';
    sub = comparedParts
      ? `They are at or below the best offer on every part another supplier priced (${plural(comparedParts, 'part', 'parts')}).`
      : 'No other supplier priced these parts, so there is nothing to compare.';
  } else if (conc.assertable) {
    const share = Math.round(Number(conc.share) * 100);
    big = share;
    head = `${plural(conc.focus_parts, 'part makes', 'parts make')} up ${share}% of the gap to the best offers`;
    sub = `${formatAmount(conc.focus_total, currency, 0)} of ${formatAmount(conc.gap_total, currency, 0)} a year, over the ${plural(conc.lower_parts, 'part', 'parts')} where another supplier offers less. The negotiation deck focuses on these ${plural(conc.focus_parts, 'part', 'parts')}.`;
  } else {
    head = `${plural(conc.lower_parts, 'part has', 'parts have')} a lower offer from another supplier`;
    sub = `${formatAmount(conc.gap_total, currency, 0)} a year in all. ${conc.focus_parts === conc.lower_parts
      ? `The negotiation deck focuses on ${conc.lower_parts === 1 ? 'it' : 'them'}.` : ''}`;
  }
  return (
    <section className="srce-card2" data-testid="neg-conc">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Where the gap concentrates</span>
        <span className="srce-mut">Gap to the best offers, per year at event volumes</span>
      </div>
      <div className="neg-conc">
        {big !== null && <span className="neg-conc-big">{big}%</span>}
        <div className="neg-conc-text">
          <div className="srce-strong">{head}</div>
          <div className="srce-mut">{sub}</div>
        </div>
        {big !== null && <div className="neg-share" aria-hidden="true"><span style={{ width: `${big}%` }} /></div>}
      </div>
    </section>
  );
}

// A price as the buyer types it: at most 4 decimals, trailing zeros past 2 dropped
// (0.9000 shows as 0.90, 0.8765 stays).
function plainPrice(value) {
  if (value === null || value === undefined || value === '') return '';
  const [whole, frac = ''] = String(value).split('.');
  const trimmed = frac.replace(/0+$/, '');
  return `${whole}.${trimmed.length >= 2 ? trimmed : trimmed.padEnd(2, '0')}`;
}

function sameNumber(a, b) {
  return a !== '' && b !== '' && Number(a) === Number(b);
}

// The target box, prefilled with the target in use (the buyer's, else the default).
// Only a change the buyer made is saved: focusing and leaving it saves nothing. An edit
// keeps the currency and unit the part had when the buyer started typing, so a change
// published meanwhile makes the server refuse it instead of saving the price in the new
// basis (Codex S3a r2 #1). While a save is in flight the box is locked until the
// refreshed figures are in, then the table redraws the box from them (a new key), so no
// newer typing can be overwritten and a reset always shows the server's value (Codex
// S3a r3 #1, #2). A refresh for another reason replaces the text only when the buyer
// has not typed.
// Read only (an awarded event): the target as text, never a locked box that reads "Set"
// (Lane 1395 QA).
function TargetText({ row, currency }) {
  const v = row.buyer_target || row.default_target;
  return v ? formatUnitPrice(v, currency) : <span className="srce-mut">-</span>;
}

function TargetInput({ row, basis, saving, error, onSave, readOnly }) {
  const shown = plainPrice(row.buyer_target || row.default_target);
  const [text, setText] = useState(shown);
  const [bad, setBad] = useState(false);
  const base = useRef(shown);           // the value the server shows now
  const dirty = useRef(false);          // the buyer typed since this box was drawn
  const editBasis = useRef(null);       // the basis when this edit began
  useEffect(() => {
    base.current = shown;
    if (!dirty.current) { setText(shown); setBad(false); }
  }, [shown]);
  const settle = () => { dirty.current = false; editBasis.current = null; setText(base.current); setBad(false); };
  const commit = () => {
    if (!dirty.current) return;
    const v = text.trim().replace(/^\$/, '').replace(/,/g, '');
    if (sameNumber(v, base.current) || (v === '' && !row.buyer_target)) { settle(); return; }
    if (v !== '' && (!TARGET_RE.test(v) || Number(v) <= 0 || Number(v) > 1e9)) { setBad(true); return; }
    setBad(false);
    onSave(v === '' ? null : v, editBasis.current || basis);
  };
  return (
    <input className={`neg-tin${bad || error ? ' neg-tin--bad' : ''}${row.buyer_target ? ' neg-tin--mine' : ''}`}
      inputMode="decimal" value={text} placeholder="Set" disabled={saving || readOnly}
      aria-label={`Target for ${row.part_number}`} aria-invalid={bad || Boolean(error)}
      title={error || (bad ? 'A price above 0 with at most 4 decimals' : undefined)}
      onChange={(e) => {
        if (!dirty.current) editBasis.current = basis;
        dirty.current = true;
        setText(e.target.value);
        setBad(false);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') settle();
      }} />
  );
}

function todayCell(t, currency) {
  if (t.state === 'varies') return <span className="neg-warn">Varies</span>;
  if (t.state === 'missing') return <span className="srce-mut">None</span>;
  const price = formatUnitPrice(t.price, currency);
  return t.state === 'partial' ? <span title="Some sites only">{price}*</span> : price;
}

// ``readOnly`` (Lane 1395): an awarded event shows its targets, never edits them.
function Targets({ targets, currency, onSave, onDismiss, readOnly }) {
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  // Per part: a save in flight, and the last refusal. This component is keyed by the
  // supplier, so a save that finishes after the buyer switched supplier changes
  // nothing here (Codex S3a r1 #5).
  const [pending, setPending] = useState({});
  const [errors, setErrors] = useState({});
  const [drawn, setDrawn] = useState({});     // per part: redraw the box after a save
  const [dismissing, setDismissing] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // ``onSave`` resolves once the refreshed figures are in; the box stays locked until then.
  const save = async (row, value, basis) => {
    const part = row.part_number;
    setPending((p) => ({ ...p, [part]: true }));
    setErrors((e) => ({ ...e, [part]: '' }));
    try {
      await onSave(row, value, basis);
      if (alive.current) setDrawn((d) => ({ ...d, [part]: (d[part] || 0) + 1 }));
    } catch (e) {
      if (alive.current) setErrors((x) => ({ ...x, [part]: e.message }));
    } finally {
      if (alive.current) setPending((p) => { const n = { ...p }; delete n[part]; return n; });
    }
  };
  const dismiss = async () => {
    setDismissing(true);
    try {
      await onDismiss();
    } catch (e) {
      if (alive.current) setErrors((x) => ({ ...x, _dismiss: e.message }));
    } finally {
      if (alive.current) setDismissing(false);
    }
  };
  const rows = targets.rows.filter((r) => filter === 'all'
    || (filter === 'target' ? r.target !== null : r.basis === 'needs_buyer_target'));
  const needs = targets.rows.filter((r) => r.basis === 'needs_buyer_target').length;
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice((Math.min(page, pages) - 1) * PAGE, Math.min(page, pages) * PAGE);
  const chips = [['all', `All ${targets.rows.length.toLocaleString()}`], ['target', `With a target ${targets.with_target.toLocaleString()}`],
    ['needs', `Needs your target ${needs.toLocaleString()}`]];
  const cleared = targets.cleared || [];
  return (
    <section className="srce-card2" data-testid="neg-targets">
      <div className="srce-card2-h neg-wrap">
        <span className="srce-card2-t">Targets</span>
        <span className="neg-chips" role="group" aria-label="Filter parts">
          {chips.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={filter === k} className={`neg-chip${filter === k ? ' neg-chip--on' : ''}`}
              onClick={() => { setFilter(k); setPage(1); }}>{label}</button>
          ))}
        </span>
        <span className="srce-push srce-mut">A blank target uses the default</span>
      </div>
      {cleared.length > 0 && (
        <div className="srce-notice neg-cleared" role="status" data-testid="neg-cleared">
          <span>
            {targets.cleared_count === 1 ? 'Your target on 1 part was cleared' : `Your targets on ${targets.cleared_count.toLocaleString()} parts were cleared`}
            {' '}because a published change changed the part, its unit or the currency: {cleared.map((c) => (
              `${c.part_number} (was ${formatUnitPrice(c.target, c.currency)} per ${c.unit}${c.amendment_number ? `, change ${c.amendment_number}` : ''})`)).join(', ')}
            {targets.cleared_count > cleared.length ? ', and more' : ''}.
          </span>
          {!readOnly && <button type="button" className="ll-clr" disabled={dismissing} onClick={dismiss}>Dismiss</button>}
        </div>
      )}
      {Object.entries(errors).filter(([, m]) => m).length > 0 && (
        <div className="srce-error neg-cleared" role="alert">
          Not saved: {Object.entries(errors).filter(([, m]) => m).map(([p, m]) => (p === '_dismiss' ? m : `${p}: ${m}`)).join(' ')}
        </div>
      )}
      <div className="neg-tw">
        <table className="neg-tbl">
          <thead>
            <tr>
              <th>Part</th><th>Description</th><th className="neg-r">Today</th><th className="neg-r">Their offer</th>
              <th className="neg-r">Best offer</th><th className="neg-r">Target</th><th className="neg-r">Reduction / yr</th><th>Basis</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={8} className="srce-mut">No parts match.</td></tr>}
            {shown.map((r) => {
              const alt = r.alternative;
              const lower = alt && Number(alt.price) < Number(r.offer);
              return (
                <tr key={r.part_number} className={r.basis === 'buyer' ? 'neg-mine' : ''}>
                  <td className="srce-num" title={r.description ? `${r.part_number}: ${r.description}` : r.part_number}>{r.part_number}</td>
                  <td className="neg-desc" title={r.description || ''}>{r.description || ''}</td>
                  <td className="neg-r">{todayCell(r.today, currency)}</td>
                  <td className="neg-r" title={`Round ${r.offer_round}, per ${r.unit}`}>{formatUnitPrice(r.offer, currency)}</td>
                  <td className="neg-r">{alt ? <>
                    <span className={lower ? 'neg-crit' : 'neg-good'} title={alt.supplier_name}>{formatUnitPrice(alt.price, currency)}</span>
                    {/* The best offer can come from a supplier not yet approved for the part
                        (Lane 1395): a small mark, the name on hover. Buyer only. */}
                    {alt.approved === false && (
                      <span className="srce-hmark neg-na" title={`${alt.supplier_name || 'This supplier'} is not approved for this part yet`}
                        aria-label="not approved">!</span>)}
                  </> : <span className="srce-mut">None</span>}</td>
                  <td className="neg-r">
                    {readOnly ? <TargetText row={r} currency={currency} /> : (
                      <TargetInput key={`${r.part_number}:${drawn[r.part_number] || 0}`} row={r}
                        basis={{ currency, unit: r.unit }} saving={Boolean(pending[r.part_number])}
                        error={errors[r.part_number]} onSave={(v, b) => save(r, v, b)} readOnly={readOnly} />)}
                  </td>
                  <td className="neg-r">{r.reduction === null ? <span className="srce-mut">None</span> : formatAmount(r.reduction, currency, 2)}</td>
                  <td className={`neg-why neg-why--${r.basis}`}
                    title={r.basis === 'hold_today' && r.default_rounded ? "Today's price, rounded down" : undefined}>
                    {BASIS_TEXT[r.basis]}
                    {r.basis === 'buyer' && !readOnly && (
                      <> · <button type="button" className="neg-lnk" disabled={Boolean(pending[r.part_number])} onClick={() => save(r, null, null)}>Reset</button></>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              {/* The description cell is its own, so the row keeps its columns when the
                  description column is hidden. */}
              <td>Total reduction</td>
              <td />
              <td colSpan={4} className="srce-mut">all {plural(targets.rows.length, 'part', 'parts')}</td>
              <td className="neg-r">{targets.reduction_total === null ? 'Not available' : formatAmount(targets.reduction_total, currency, 2)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {pages > 1 && (
        <div className="srce-card2-f srce-row">
          <button type="button" className="ll-clr" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="srce-mut">Page {Math.min(page, pages)} of {pages}</span>
          <button type="button" className="ll-clr" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
      <div className="srce-card2-f srce-mut">
        Target: Wingman fills it in. It matches the best offer when that offer is lower, or holds today's price when their offer is an increase. The best offer is the lowest offer from any other supplier. A ! marks a supplier not yet approved for the part. Type over a target to set your own. When today's price varies, you set it. Reduction is per year at the event's annual quantities.
      </div>
    </section>
  );
}

export default function NegotiationTab({ api, eventId, refreshKey, onOpenOverview, onOpenRound }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [supplierId, setSupplierId] = useState(null);
  const sel = useRef(null);      // the supplier the buyer chose (null: the default)
  const seq = useRef(0);         // only the newest read may show
  const applied = useRef(0);     // the newest read whose figures are on screen
  const waiters = useRef([]);    // saves waiting for a read at or after theirs to show
  const alive = useRef(true);
  const settle = (ok, upTo, err) => {
    const now = waiters.current;
    waiters.current = now.filter((w) => w.n > upTo);
    now.filter((w) => w.n <= upTo).forEach((w) => (ok ? w.resolve() : w.reject(err)));
  };
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; settle(false, Infinity, new Error('The tab closed.')); };
  }, []);
  const choose = (sid) => { sel.current = sid; setSupplierId(sid); };
  // One read. Returns its number; only the newest read's figures are shown. When they
  // are, every save waiting for a read at or before this one is done; when the newest
  // read fails, every waiting save is told (Codex S3a r4 #1, r5 #1).
  const load = useCallback((sid) => {
    const mine = ++seq.current;
    api.negotiation(eventId, sid).then((d) => {
      if (!alive.current || mine !== seq.current) return;
      setData(d);
      setError('');
      applied.current = mine;
      settle(true, mine);
    }, (e) => {
      if (!alive.current || mine !== seq.current) return;
      // The chosen supplier left the picker (no offer and no target left): the default.
      if (e.code === 'supplier_not_found' && sid !== null) {
        sel.current = null;
        setSupplierId(null);
        return;
      }
      setError(e.message);
      settle(false, Infinity, new Error('Saved, but the figures could not be refreshed. Reload the tab.'));
    });
    return mine;
  }, [api, eventId]);
  // Resolves once a read started at or after read ``n`` is on screen.
  const shown = (n) => (applied.current >= n ? Promise.resolve()
    : new Promise((resolve, reject) => { waiters.current.push({ n, resolve, reject }); }));
  useEffect(() => { load(supplierId); }, [load, supplierId, refreshKey]);

  if (error && !data) return <div className="srce-error" role="alert">{error}</div>;
  if (!data) return <div className="ll-loading">Loading…</div>;
  const currency = data.event.currency;
  const sup = data.supplier;
  // Lane 1395: after the award the tab stays readable; nothing on it can change.
  const awarded = data.event.status === 'awarded';

  // ``basis``: the currency and unit the buyer typed against (never the refreshed ones).
  // Resolves after the tab shows the server's figures again; a refusal is thrown to the
  // targets table (it shows it on that row) after the refresh.
  const saveTarget = (row, value, basis) => api.negotiationAction(eventId, 'targets', value === null
    ? { supplier_id: sup.supplier_id, part_number: row.part_number, target: null }
    : { supplier_id: sup.supplier_id, part_number: row.part_number, target: value, ...basis })
    .then(() => shown(load(sel.current)), (e) => shown(load(sel.current)).then(() => { throw e; }, () => { throw e; }));
  // A supplier listed only for its cleared targets leaves the picker once they are
  // dismissed: fall back to the default, but only if the buyer still has it chosen
  // (Codex S3a r2 #4, r3 #4).
  const dismiss = () => {
    const dismissed = sup.supplier_id;
    const noOffers = !sup.parts_priced;
    return api.negotiationAction(eventId, 'targets/dismiss-cleared', { supplier_id: dismissed })
      .then(() => {
        if (sel.current === dismissed && noOffers) { choose(null); return undefined; }
        if (sel.current === dismissed || sel.current === null) return shown(load(sel.current));
        return undefined;
      });
  };

  return (
    <div className="srce-ov neg" data-testid="neg-tab">
      {error && (
        <div className="srce-error" role="alert">{error} <button type="button" className="ll-clr" onClick={() => load(sel.current)}>Try again</button></div>)}
      <div className="neg-head">
        <label className="neg-k" htmlFor="neg-sup">Supplier</label>
        {data.suppliers.length > 0 ? (
          <select id="neg-sup" className="srce-input neg-picker" value={sup ? sup.supplier_id : ''}
            onChange={(e) => choose(Number(e.target.value))}>
            {data.suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>)}
          </select>
        ) : <span className="srce-mut">No supplier has priced a part yet.</span>}
        {sup && (
          <span className="srce-mut">
            {[sup.today_supplier ? "Today's supplier" : null, plural(sup.parts_priced, 'part quoted', 'parts quoted'),
              sup.latest_offer_at ? `latest offer ${when(sup.latest_offer_at)}` : null].filter(Boolean).join(' · ')}
          </span>
        )}
        {sup && sup.parts_priced > 0 && data.feedback && !awarded && (
          <NegotiationActions key={`actions-${eventId}-${sup.supplier_id}`} api={api} eventId={eventId} supplier={sup}
            feedback={data.feedback} deck={data.deck || null} onChanged={() => load(sel.current)}
            onDeckChanged={() => shown(load(sel.current))} onOpenRound={onOpenRound} />
        )}
      </div>
      <div className="neg-grid2">
        <Strategy goal={data.event.goal} approach={data.approach} onOpenOverview={awarded ? null : onOpenOverview} fit={Boolean(sup)} />
        {sup && <Stand stand={data.stand} supplierName={sup.supplier_name} currency={currency} />}
      </div>
      {sup && <Concentration conc={data.concentration} currency={currency} comparedParts={data.stand.parts_compared} />}
      {sup && (
        <Targets key={`targets-${eventId}-${sup.supplier_id}`} targets={data.targets} currency={currency}
          onSave={saveTarget} onDismiss={dismiss} readOnly={awarded} />
      )}
      {sup && (
        <NegotiationIntel key={`intel-${eventId}-${sup.supplier_id}`} api={api} eventId={eventId}
          supplierId={sup.supplier_id} supplierName={sup.supplier_name} refreshKey={refreshKey} />
      )}
    </div>
  );
}
